import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Contract, ContractCreateDto, MilestonePaymentLog, MilestonePaymentLogCreateDto } from "../../modules/shared/model";
import { 
	createContract as createContractAction, 
	updateContract as updateContractAction, 
	getContractById as getContractByIdAction, 
	getContracts as getContractsAction, 
	deleteContract as deleteContractAction, 
	uploadContractFile as uploadContractFileAction,
	createMilestonePaymentLog as createMilestonePaymentLogAction,
	getMilestonePaymentLogs as getMilestonePaymentLogsAction,
	getMilestonePaymentLogById as getMilestonePaymentLogByIdAction,
	getMilestonePaymentLogsByMilestone as getMilestonePaymentLogsByMilestoneAction,
	updateMilestonePaymentLog as updateMilestonePaymentLogAction,
	deleteMilestonePaymentLog as deleteMilestonePaymentLogAction,
	getEmergencyContracts as getEmergencyContractsAction
} from "../Actions/ContractActions";

export const createContract = createAsyncThunk("contracts/create", async (data: ContractCreateDto, { rejectWithValue }) => {
	try { return await createContractAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create contract"); }
});

export const updateContract = createAsyncThunk("contracts/update", async ({ id, data }: { id: string; data: Partial<ContractCreateDto> }, { rejectWithValue }) => {
	try { return await updateContractAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update contract"); }
});

export const fetchContract = createAsyncThunk("contracts/getById", async (id: string, { rejectWithValue }) => {
	try { return await getContractByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch contract"); }
});

export const fetchContracts = createAsyncThunk("contracts/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getContractsAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch contracts"); }
});

export const deleteContract = createAsyncThunk("contracts/delete", async (id: string, { rejectWithValue }) => {
	try { await deleteContractAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete contract"); }
});

export const uploadContractFile = createAsyncThunk("contracts/uploadFile", async (file: File, { rejectWithValue }) => {
	try { 
		return await uploadContractFileAction(file);
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to upload file"); 
	}
});

// Milestone Payment Log Async Thunks
export const createMilestonePaymentLog = createAsyncThunk("contracts/createMilestonePaymentLog", async (data: MilestonePaymentLogCreateDto, { rejectWithValue }) => {
	try { return await createMilestonePaymentLogAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create milestone payment log"); }
});

export const fetchMilestonePaymentLogs = createAsyncThunk("contracts/fetchMilestonePaymentLogs", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getMilestonePaymentLogsAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch milestone payment logs"); }
});

export const fetchMilestonePaymentLog = createAsyncThunk("contracts/fetchMilestonePaymentLog", async (id: string, { rejectWithValue }) => {
	try { return await getMilestonePaymentLogByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch milestone payment log"); }
});

export const fetchMilestonePaymentLogsByMilestone = createAsyncThunk("contracts/fetchMilestonePaymentLogsByMilestone", async (milestoneId: string, { rejectWithValue }) => {
	try { return await getMilestonePaymentLogsByMilestoneAction(milestoneId); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch milestone payment logs by milestone"); }
});

export const updateMilestonePaymentLog = createAsyncThunk("contracts/updateMilestonePaymentLog", async ({ id, data }: { id: string; data: Partial<MilestonePaymentLogCreateDto> }, { rejectWithValue }) => {
	try { return await updateMilestonePaymentLogAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update milestone payment log"); }
});

export const deleteMilestonePaymentLog = createAsyncThunk("contracts/deleteMilestonePaymentLog", async (id: string, { rejectWithValue }) => {
	try { await deleteMilestonePaymentLogAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete milestone payment log"); }
});

// Emergency Contracts Async Thunks
export const fetchEmergencyContracts = createAsyncThunk("contracts/fetchEmergencyContracts", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getEmergencyContractsAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch emergency contracts"); }
});

interface ContractsState {
	items: Contract[];
	current: Contract | null;
	loading: boolean;
	error: string | null;
	totalCount?: number;
	uploading: boolean;
	uploadError: string | null;
	// Milestone Payment Logs
	milestonePaymentLogs: MilestonePaymentLog[];
	currentMilestonePaymentLog: MilestonePaymentLog | null;
	milestonePaymentLogsLoading: boolean;
	milestonePaymentLogsError: string | null;
	milestonePaymentLogsTotalCount?: number;
	// Emergency Contracts
	emergencyContracts: Contract[];
	emergencyContractsLoading: boolean;
	emergencyContractsError: string | null;
	emergencyContractsTotalCount?: number;
}

const initialState: ContractsState = {
	items: [],
	current: null,
	loading: false,
	error: null,
	totalCount: 0,
	uploading: false,
	uploadError: null,
	// Milestone Payment Logs
	milestonePaymentLogs: [],
	currentMilestonePaymentLog: null,
	milestonePaymentLogsLoading: false,
	milestonePaymentLogsError: null,
	milestonePaymentLogsTotalCount: 0,
	// Emergency Contracts
	emergencyContracts: [],
	emergencyContractsLoading: false,
	emergencyContractsError: null,
	emergencyContractsTotalCount: 0,
};

const contractsSlice = createSlice({
	name: "contracts",
	initialState,
	reducers: {
		clearContractsError(state) { state.error = null; },
		clearCurrentContract(state) { state.current = null; },
		clearUploadError(state) { state.uploadError = null; },
		// Milestone Payment Logs
		clearMilestonePaymentLogsError(state) { state.milestonePaymentLogsError = null; },
		clearCurrentMilestonePaymentLog(state) { state.currentMilestonePaymentLog = null; },
		clearMilestonePaymentLogs(state) {
			state.milestonePaymentLogs = [];
			state.currentMilestonePaymentLog = null;
			state.milestonePaymentLogsTotalCount = 0;
		},
		// Emergency Contracts
		clearEmergencyContractsError(state) { state.emergencyContractsError = null; },
		clearEmergencyContracts(state) {
			state.emergencyContracts = [];
			state.emergencyContractsTotalCount = 0;
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(createContract.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(createContract.fulfilled, (s, a: PayloadAction<Contract>) => { s.loading = false; s.items.unshift(a.payload); s.totalCount = (s.totalCount || 0) + 1; })
			.addCase(createContract.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(updateContract.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(updateContract.fulfilled, (s, a: PayloadAction<Contract>) => {
				s.loading = false;
				const i = s.items.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.items[i] = a.payload;
				if (s.current?.id === a.payload.id) s.current = a.payload;
			})
			.addCase(updateContract.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchContract.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchContract.fulfilled, (s, a: PayloadAction<Contract>) => { s.loading = false; s.current = a.payload; })
			.addCase(fetchContract.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchContracts.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchContracts.fulfilled, (s, a: PayloadAction<{ data: Contract[]; totalCount?: number }>) => { s.loading = false; s.items = a.payload.data || []; s.totalCount = a.payload.totalCount || 0; })
			.addCase(fetchContracts.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(deleteContract.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(deleteContract.fulfilled, (s, a: PayloadAction<string>) => { s.loading = false; s.items = s.items.filter(x => x.id !== a.payload); if (s.current?.id === a.payload) s.current = null; s.totalCount = Math.max(0, (s.totalCount || 0) - 1); })
			.addCase(deleteContract.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(uploadContractFile.pending, (s) => { s.uploading = true; s.uploadError = null; })
			.addCase(uploadContractFile.fulfilled, (s) => { s.uploading = false; })
			.addCase(uploadContractFile.rejected, (s, a: any) => { s.uploading = false; s.uploadError = a.payload as string; });

		// Milestone Payment Logs Extra Reducers
		builder
			.addCase(createMilestonePaymentLog.pending, (s) => { s.milestonePaymentLogsLoading = true; s.milestonePaymentLogsError = null; })
			.addCase(createMilestonePaymentLog.fulfilled, (s, a: PayloadAction<MilestonePaymentLog>) => { s.milestonePaymentLogsLoading = false; s.milestonePaymentLogs.unshift(a.payload); s.milestonePaymentLogsTotalCount = (s.milestonePaymentLogsTotalCount || 0) + 1; })
			.addCase(createMilestonePaymentLog.rejected, (s, a: any) => { s.milestonePaymentLogsLoading = false; s.milestonePaymentLogsError = a.payload as string; });

		builder
			.addCase(fetchMilestonePaymentLogs.pending, (s) => { s.milestonePaymentLogsLoading = true; s.milestonePaymentLogsError = null; })
			.addCase(fetchMilestonePaymentLogs.fulfilled, (s, a: PayloadAction<{ data: MilestonePaymentLog[]; totalCount?: number }>) => { s.milestonePaymentLogsLoading = false; s.milestonePaymentLogs = a.payload.data || []; s.milestonePaymentLogsTotalCount = a.payload.totalCount || 0; })
			.addCase(fetchMilestonePaymentLogs.rejected, (s, a: any) => { s.milestonePaymentLogsLoading = false; s.milestonePaymentLogsError = a.payload as string; });

		builder
			.addCase(fetchMilestonePaymentLog.pending, (s) => { s.milestonePaymentLogsLoading = true; s.milestonePaymentLogsError = null; })
			.addCase(fetchMilestonePaymentLog.fulfilled, (s, a: PayloadAction<MilestonePaymentLog>) => { s.milestonePaymentLogsLoading = false; s.currentMilestonePaymentLog = a.payload; })
			.addCase(fetchMilestonePaymentLog.rejected, (s, a: any) => { s.milestonePaymentLogsLoading = false; s.milestonePaymentLogsError = a.payload as string; });

		builder
			.addCase(fetchMilestonePaymentLogsByMilestone.pending, (s) => { s.milestonePaymentLogsLoading = true; s.milestonePaymentLogsError = null; })
			.addCase(fetchMilestonePaymentLogsByMilestone.fulfilled, (s, a: PayloadAction<{ data: MilestonePaymentLog[]; totalCount?: number }>) => { s.milestonePaymentLogsLoading = false; s.milestonePaymentLogs = a.payload.data || []; s.milestonePaymentLogsTotalCount = a.payload.totalCount || 0; })
			.addCase(fetchMilestonePaymentLogsByMilestone.rejected, (s, a: any) => { s.milestonePaymentLogsLoading = false; s.milestonePaymentLogsError = a.payload as string; });

		builder
			.addCase(updateMilestonePaymentLog.pending, (s) => { s.milestonePaymentLogsLoading = true; s.milestonePaymentLogsError = null; })
			.addCase(updateMilestonePaymentLog.fulfilled, (s, a: PayloadAction<MilestonePaymentLog>) => {
				s.milestonePaymentLogsLoading = false;
				const i = s.milestonePaymentLogs.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.milestonePaymentLogs[i] = a.payload;
				if (s.currentMilestonePaymentLog?.id === a.payload.id) s.currentMilestonePaymentLog = a.payload;
			})
			.addCase(updateMilestonePaymentLog.rejected, (s, a: any) => { s.milestonePaymentLogsLoading = false; s.milestonePaymentLogsError = a.payload as string; });

		builder
			.addCase(deleteMilestonePaymentLog.pending, (s) => { s.milestonePaymentLogsLoading = true; s.milestonePaymentLogsError = null; })
			.addCase(deleteMilestonePaymentLog.fulfilled, (s, a: PayloadAction<string>) => { s.milestonePaymentLogsLoading = false; s.milestonePaymentLogs = s.milestonePaymentLogs.filter(x => x.id !== a.payload); if (s.currentMilestonePaymentLog?.id === a.payload) s.currentMilestonePaymentLog = null; s.milestonePaymentLogsTotalCount = Math.max(0, (s.milestonePaymentLogsTotalCount || 0) - 1); })
			.addCase(deleteMilestonePaymentLog.rejected, (s, a: any) => { s.milestonePaymentLogsLoading = false; s.milestonePaymentLogsError = a.payload as string; });

		// Emergency Contracts Extra Reducers
		builder
			.addCase(fetchEmergencyContracts.pending, (s) => { s.emergencyContractsLoading = true; s.emergencyContractsError = null; })
			.addCase(fetchEmergencyContracts.fulfilled, (s, a: PayloadAction<{ data: Contract[]; totalCount?: number }>) => { s.emergencyContractsLoading = false; s.emergencyContracts = a.payload.data || []; s.emergencyContractsTotalCount = a.payload.totalCount || 0; })
			.addCase(fetchEmergencyContracts.rejected, (s, a: any) => { s.emergencyContractsLoading = false; s.emergencyContractsError = a.payload as string; });
	}
});

export const { 
	clearContractsError, 
	clearCurrentContract, 
	clearUploadError,
	clearMilestonePaymentLogsError,
	clearCurrentMilestonePaymentLog,
	clearMilestonePaymentLogs,
	clearEmergencyContractsError,
	clearEmergencyContracts
} = contractsSlice.actions;
export default contractsSlice.reducer;

